import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, bulkSuspendUsers } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
interface SuspendUsersProps {
  open: boolean;
  handleClose: () => void;
  selectedUsers: string[];
  setSelectedUsers: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function SuspendUsersDialog({
  open,
  handleClose,
  selectedUsers,
  setSelectedUsers,
  changeLogRoles,
}: SuspendUsersProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending: isSuspending } = useMutation({
    mutationFn: () => bulkSuspendUsers('User', selectedUsers, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'The task to suspend the user(s) has started.',
      });
      selectedUsers.forEach((user) => {
        queryClient.invalidateQueries({ queryKey: [UserKeys.User, user] });
      });
      setChangeLogMessage('');
      setSelectedUsers([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue suspending the selected user(s). Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_grid.bulk_suspend_users, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.name,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, UserRoles.BulkSuspendUsers.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={selectedUsers.length > 1 ? 'Suspend Users' : 'Suspend User'}
      dialogContent={
        <Box>
          {selectedUsers.length > 1 ? (
            <Typography>
              Suspend <strong>{selectedUsers.length} users</strong>.
            </Typography>
          ) : (
            <Typography>
              Suspend <strong>{selectedUsers[0]}</strong>.
            </Typography>
          )}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isSuspending}
          />
        </Box>
      }
      primaryButton={{
        loading: isSuspending,
        action: handleSubmit,
        disabled: isSuspending,
        title: selectedUsers.length > 1 ? 'Suspend Users' : 'Suspend User',
        color: 'warning',
      }}
    />
  );
}
